import React, { useState, useEffect } from 'react';
import { Award, Cloud, TrendingDown, Container, Lock, Activity, CheckCircle, ChevronDown, ChevronUp, Zap, Users, Globe, Briefcase, Target, Star, Quote, ArrowRight, Play, GitBranch, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function Home() {
  const [activeSection, setActiveSection] = useState('home');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentCaseStudy, setCurrentCaseStudy] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'case-studies', 'testimonials', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);

      // Show scroll to top button when scrolled down more than 400px
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      icon: <Cloud className="w-12 h-12" />,
      title: "Cloud Architecture & Migration",
      description: "Transform your infrastructure with enterprise-grade AWS and Azure architectures. From legacy systems to modern cloud-native solutions, we design for scale, security, and cost-efficiency.",
      features: ["Multi-region deployment", "High availability design", "Disaster recovery planning", "Cloud cost optimization"],
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&auto=format&fit=crop&q=80",
      stats: { projects: "50+", savings: "40%" },
      slug: "/services/cloud-architecture"
    },
    {
      icon: <GitBranch className="w-12 h-12" />,
      title: "DevOps CI/CD Automation",
      description: "Accelerate your software delivery with fully automated CI/CD pipelines. Integrate testing, security scanning, and deployment orchestration for continuous innovation.",
      features: ["Automated testing", "Security integration", "Zero-downtime deployment", "Rollback strategies"],
      image: "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=1200&auto=format&fit=crop&q=80",
      stats: { projects: "100+", improvement: "60%" },
      slug: "/services/devops-automation"
    },
    {
      icon: <Container className="w-12 h-12" />,
      title: "Kubernetes & Container Management",
      description: "Master container orchestration with production-ready Kubernetes deployments. Auto-scaling, service mesh, and comprehensive monitoring for your microservices.",
      features: ["Auto-scaling", "Service mesh", "RBAC configuration", "Helm charts"],
      image: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=1200&auto=format&fit=crop&q=80",
      stats: { clusters: "30+", uptime: "99.99%" },
      slug: "/services/kubernetes-management"
    },
    {
      icon: <Lock className="w-12 h-12" />,
      title: "Security & Compliance",
      description: "Achieve SOC 2, PCI-DSS, HIPAA, and GDPR compliance with comprehensive security audits and automated monitoring. Protect your infrastructure and customer data.",
      features: ["Compliance audits", "Vulnerability scanning", "Security automation", "Incident response"],
      image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1200&auto=format&fit=crop&q=80",
      stats: { audits: "25+", incidents: "0" },
      slug: "/services/security-compliance"
    },
    {
      icon: <TrendingDown className="w-12 h-12" />,
      title: "FinOps & Cost Optimization",
      description: "Reduce cloud spending by 30-40% through intelligent resource management, right-sizing, and automated governance policies. Maximize ROI on cloud investments.",
      features: ["Cost analysis", "Resource optimization", "Budget alerts", "Reserved instances"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80",
      stats: { saved: "$2M+", optimization: "35%" },
      slug: "/services/finops-cost-optimization"
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: "Monitoring & Observability",
      description: "Gain complete visibility into your infrastructure with real-time monitoring, logging, and alerting. Proactive incident detection and resolution.",
      features: ["Real-time monitoring", "Log aggregation", "Custom dashboards", "Alert management"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80",
      stats: { systems: "200+", mttr: "15min" },
      slug: "/services/monitoring-observability"
    }
  ];

  const caseStudies = [
    {
      title: "Global Fintech Platform Transformation",
      challenge: "Legacy monolithic architecture causing scalability issues, frequent downtime, and slow feature deployment. Processing 10M+ transactions daily with growing security concerns.",
      solution: "Migrated to AWS EKS with 22 microservices architecture. Implemented comprehensive CI/CD pipelines with GitOps, automated security scanning, and blue-green deployments. Set up cross-account ECR for secure image management and IRSA for granular permissions.",
      results: [
        { metric: "Uptime Improvement", value: "95% → 99.99%", impact: "Eliminated revenue loss from downtime" },
        { metric: "Deployment Speed", value: "2 days → 2 hours", impact: "40% faster feature delivery" },
        { metric: "Infrastructure Cost", value: "Reduced by 35%", impact: "$450K annual savings" },
        { metric: "Security Incidents", value: "Zero breaches", impact: "Achieved PCI-DSS compliance" }
      ],
      technologies: ["AWS EKS", "Terraform", "GitHub Actions", "ArgoCD", "Prometheus", "Grafana"],
      image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&auto=format&fit=crop&q=80",
      industry: "Fintech",
      duration: "6 months",
      teamSize: "8 engineers"
    },
    {
      title: "Multi-Tenant SaaS Infrastructure",
      challenge: "Building compliant multi-tenant SaaS platform from scratch requiring SOC 2 Type II certification, automated client onboarding, and isolated tenant environments with shared infrastructure.",
      solution: "Architected AWS-native multi-tenant infrastructure with automated client provisioning using Lambda and EventBridge. Implemented cross-account ECR architecture, Secrets Manager integration, and comprehensive audit logging for compliance.",
      results: [
        { metric: "SOC 2 Certification", value: "Achieved in 4 months", impact: "Enterprise customer acquisition" },
        { metric: "Client Onboarding", value: "Manual → 15 minutes", impact: "90% time reduction" },
        { metric: "Infrastructure Cost", value: "30% optimization", impact: "$200K savings per year" },
        { metric: "Security Score", value: "98/100", impact: "Industry-leading security" }
      ],
      technologies: ["AWS Lambda", "EventBridge", "Secrets Manager", "Terraform", "CloudWatch", "VPC Peering"],
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&auto=format&fit=crop&q=80",
      industry: "SaaS",
      duration: "8 months",
      teamSize: "6 engineers"
    },
    {
      title: "Banking Digital Transformation",
      challenge: "Operating across 180+ countries with inconsistent infrastructure, compliance challenges (PCI-DSS, GDPR), and 24/7 uptime requirements for core banking systems.",
      solution: "Led hybrid AWS & Azure infrastructure transformation with automated disaster recovery, real-time data replication, and multi-region deployment. Implemented comprehensive security controls and automated compliance monitoring.",
      results: [
        { metric: "Global Availability", value: "99.99% uptime", impact: "Zero service interruptions" },
        { metric: "Compliance", value: "PCI-DSS + GDPR", impact: "Regulatory approval in all markets" },
        { metric: "Team Efficiency", value: "6 engineers managed", impact: "Scaled operations globally" },
        { metric: "Incident Response", value: "60min → 5min", impact: "92% MTTR improvement" }
      ],
      technologies: ["AWS", "Azure", "Chef", "Jenkins", "Terraform", "Datadog", "CloudFlare"],
      image: "https://images.unsplash.com/photo-1601597111158-2fceff292cdc?w=1200&auto=format&fit=crop&q=80",
      industry: "Banking",
      duration: "12 months",
      teamSize: "6 engineers"
    },
    {
      title: "Azure Cost Optimization",
      challenge: "Enterprise client paying $20K monthly across multiple Azure subscriptions with no cost visibility, resource sprawl, and unoptimized infrastructure leading to significant waste.",
      solution: "Reengineered and optimized Azure architecture with right-sizing analysis, reserved instances implementation, automated resource management, and comprehensive cost tracking dashboards.",
      results: [
        { metric: "Monthly Cost", value: "$20K → $13-14K", impact: "30-35% cost reduction" },
        { metric: "Annual Savings", value: "$72-84K", impact: "Immediate ROI" },
        { metric: "Cost Visibility", value: "0% → 100%", impact: "Full cost attribution by service" },
        { metric: "Resource Utilization", value: "+45%", impact: "Eliminated waste" }
      ],
      technologies: ["Azure", "Azure Cost Management", "Terraform", "Azure Monitor", "PowerBI", "Azure Advisor"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&auto=format&fit=crop&q=80",
      industry: "Enterprise",
      duration: "3 months",
      teamSize: "4 engineers"
    },
    {
      title: "HIPAA Compliant AWS Architecture",
      challenge: "Healthcare application requiring HIPAA compliance with end-to-end encryption, comprehensive audit logging, and strict access controls while maintaining high performance and availability.",
      solution: "Designed and implemented secure AWS architecture with KMS encryption for data at rest and in transit, VPC isolation, CloudTrail auditing, automated compliance checks, and disaster recovery planning.",
      results: [
        { metric: "HIPAA Compliance", value: "100% Certified", impact: "Ready for healthcare data" },
        { metric: "Security Score", value: "98/100", impact: "AWS Security Hub validation" },
        { metric: "Data Encryption", value: "100% Coverage", impact: "All data encrypted" },
        { metric: "Audit Coverage", value: "Complete", impact: "All API calls logged" }
      ],
      technologies: ["AWS", "KMS", "CloudTrail", "GuardDuty", "Security Hub", "WAF", "VPC"],
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop&q=80",
      industry: "Healthcare",
      duration: "5 months",
      teamSize: "5 engineers"
    },
    {
      title: "SaaS DevSecOps Implementation",
      challenge: "SaaS application with manual deployments taking days, no automated security scanning, compliance gaps, and frequent security vulnerabilities reaching production.",
      solution: "Implemented comprehensive DevSecOps pipeline with automated security scanning (SAST, DAST, SCA), compliance checks, zero-downtime blue-green deployments, and automated rollback capabilities.",
      results: [
        { metric: "Deployment Time", value: "2 days → 15 min", impact: "85% faster releases" },
        { metric: "Security Issues", value: "-92%", impact: "Proactive vulnerability detection" },
        { metric: "SOC 2 Compliance", value: "Achieved", impact: "Automated compliance" },
        { metric: "MTTR", value: "2hrs → 20min", impact: "70% incident reduction" }
      ],
      technologies: ["Jenkins", "SonarQube", "Snyk", "Terraform", "Kubernetes", "ArgoCD", "Prometheus"],
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&auto=format&fit=crop&q=80",
      industry: "SaaS",
      duration: "4 months",
      teamSize: "6 engineers"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "CTO",
      company: "Leading Payment Gateway",
      content: "Aneesh transformed our infrastructure from a monolithic nightmare to a scalable microservices architecture. The 99.99% uptime and 40% faster deployments have been game-changing for our business. His expertise in AWS EKS and security compliance is unmatched.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&auto=format&fit=crop&q=80",
      project: "AWS EKS Migration"
    },
    {
      name: "Michael Rodriguez",
      role: "VP Engineering",
      company: "Multi-Tenant SaaS Platform",
      content: "Working with Aneesh was exceptional. He architected our entire SOC 2 compliant infrastructure and reduced client onboarding from days to 15 minutes. His automation expertise saved us $200K annually. Highly recommend for any serious cloud project.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&auto=format&fit=crop&q=80",
      project: "Multi-Tenant Architecture"
    },
    {
      name: "David Thompson",
      role: "Head of Infrastructure",
      company: "Global Digital Banking",
      content: "Aneesh led our global infrastructure transformation across 180+ countries. His deep knowledge of compliance (PCI-DSS, GDPR) and hybrid cloud architecture ensured zero downtime during migration. A true DevOps expert who delivers results.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&auto=format&fit=crop&q=80",
      project: "Banking Digital Transformation"
    },
    {
      name: "Emily Watson",
      role: "Director of Operations",
      company: "Enterprise SaaS",
      content: "Aneesh's cost optimization strategies reduced our AWS spend by 35% while improving performance. His Terraform expertise and FinOps knowledge are invaluable. Every startup scaling their cloud infrastructure needs someone like him.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&auto=format&fit=crop&q=80",
      project: "Cloud Cost Optimization"
    }
  ];

  const achievements = [
    { icon: <Globe className="w-8 h-8" />, value: "180+", label: "Countries Served" },
    { icon: <Users className="w-8 h-8" />, value: "50+", label: "Enterprise Clients" },
    { icon: <Briefcase className="w-8 h-8" />, value: "150+", label: "Projects Completed" },
    { icon: <Award className="w-8 h-8" />, value: "99.99%", label: "Average Uptime" },
    { icon: <TrendingDown className="w-8 h-8" />, value: "$5M+", label: "Cost Savings Delivered" },
    { icon: <Target className="w-8 h-8" />, value: "100%", label: "Client Satisfaction" }
  ];

  const Logo = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="url(#gradient)" />
      <path d="M14 14h20v4h-16v6h14v4h-14v6h16v4H14V14z" fill="white" opacity="0.9"/>
      <path d="M18 20h12v2h-12v-2zm0 6h10v2h-10v-2z" fill="white"/>
      <circle cx="38" cy="10" r="6" fill="#10B981"/>
      <path d="M36 10l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <defs>
        <linearGradient id="gradient" x1="0" y1="0" x2="48" y2="48">
          <stop offset="0%" stopColor="#9333EA"/>
          <stop offset="100%" stopColor="#6366F1"/>
        </linearGradient>
      </defs>
    </svg>
  );

  const Clock = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out; }
      `}</style>

      <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1639322537228-f564d14f3312?w=1920&auto=format&fit=crop&q=80"
            alt="Tech Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-indigo-900/90 to-purple-900/95"></div>
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-6 animate-fadeInUp">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span>13+ Years of Cloud Excellence</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Transform Your<br/>
                <span className="bg-gradient-to-r from-yellow-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  Cloud Infrastructure
                </span>
              </h1>
              
              <p className="text-xl text-gray-200 leading-relaxed">
                Hi, I'm <span className="font-bold text-white">Aneesh Kumar</span> - Senior DevOps Engineer & Cloud Architect specializing in AWS, Azure, Kubernetes, and enterprise-grade security compliance.
              </p>

              <p className="text-lg text-gray-300">
                Helping startups and enterprises achieve 99.99% uptime, reduce cloud costs by 40%, and accelerate deployment velocity with modern DevSecOps practices.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <a href="#case-studies"
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-2xl flex items-center gap-2">
                  View Case Studies <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#contact"
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold transition-all flex items-center gap-2">
                  <Play className="w-5 h-5" /> Schedule a Call
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8">
                {[
                  { value: '99.99%', label: 'Uptime' },
                  { value: '40%', label: 'Cost Savings' },
                  { value: '150+', label: 'Projects' }
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-3xl font-bold text-yellow-400">{stat.value}</div>
                    <div className="text-sm text-gray-300">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative animate-float hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-pink-400/20 rounded-3xl blur-3xl"></div>
              <img
                src="/aneesh.jpg"
                alt="Aneesh Kumar"
                className="relative rounded-3xl shadow-2xl border-4 border-white/20"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-2xl animate-float">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="text-sm font-bold mt-2 text-gray-900">AWS Certified</div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-2xl animate-float" style={{animationDelay: '1s'}}>
                <Cloud className="w-8 h-8 text-blue-600" />
                <div className="text-sm font-bold mt-2 text-gray-900">Azure Certified</div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white" />
          </div>
        </div>
      </section>

      <section className="py-12 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {achievements.map((achievement, idx) => (
              <div key={idx} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl mb-3 group-hover:scale-110 transition-transform">
                  {achievement.icon}
                </div>
                <div className="text-3xl font-bold mb-1">{achievement.value}</div>
                <div className="text-sm text-purple-100">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">About Me</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&auto=format&fit=crop&q=80"
                alt="Professional workspace"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900">
                Transforming Cloud Infrastructure<br/>for <span className="text-purple-600">Global Enterprises</span>
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                With over 13 years of hands-on experience, I've architected and secured cloud infrastructures for fintech leaders, SaaS platforms, and banking institutions across 180+ countries.
              </p>
              <p className="text-gray-600 leading-relaxed">
                My expertise spans AWS, Azure, Kubernetes, Terraform, and comprehensive DevSecOps practices. I've led teams, achieved multiple compliance certifications (SOC 2, PCI-DSS, HIPAA), and delivered measurable business impact through automation and optimization.
              </p>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { label: 'AWS Certified', value: 'Professional' },
                  { label: 'Azure Expert', value: 'Architect' },
                  { label: 'Team Leadership', value: '6+ Engineers' },
                  { label: 'Compliance', value: 'SOC2, PCI-DSS' }
                ].map((item) => (
                  <div key={item.label} className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="text-sm text-gray-600">{item.label}</div>
                    <div className="text-lg font-bold text-purple-600">{item.value}</div>
                  </div>
                ))}
              </div>

              <a href="#contact"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-full font-bold hover:shadow-xl transition-all inline-flex items-center gap-2">
                Let's Discuss Your Project <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Services</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cloud and DevOps solutions tailored to accelerate your digital transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div key={idx} className="group bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all overflow-hidden border border-gray-100">
                <div className="relative h-56 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 to-transparent"></div>
                  <div className="absolute bottom-4 left-6 right-6">
                    <div className="flex items-center justify-between text-white">
                      <div>
                        <div className="text-sm opacity-90">{service.stats.projects || service.stats.clusters || service.stats.audits || service.stats.systems} Projects</div>
                        <div className="text-2xl font-bold">{service.stats.savings || service.stats.improvement || service.stats.uptime || service.stats.incidents || service.stats.optimization || service.stats.mttr}</div>
                      </div>
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        {service.icon}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a href={service.slug} className="text-purple-600 font-semibold hover:gap-3 transition-all flex items-center gap-2">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="case-studies" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Case Studies</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world transformations delivering measurable business impact
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Current Case Study */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="grid lg:grid-cols-5 gap-0">
                <div className="lg:col-span-2 relative h-64 lg:h-auto">
                  <img src={caseStudies[currentCaseStudy].image} alt={caseStudies[currentCaseStudy].title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-transparent"></div>
                  <div className="absolute top-6 left-6">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-bold text-purple-600">
                      {caseStudies[currentCaseStudy].industry}
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-3 p-8 lg:p-12">
                  <h3 className="text-3xl font-bold mb-6 text-gray-900">{caseStudies[currentCaseStudy].title}</h3>

                  <div className="space-y-4 mb-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        Challenge
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{caseStudies[currentCaseStudy].challenge}</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Solution
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{caseStudies[currentCaseStudy].solution}</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-600" />
                      Key Results
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {caseStudies[currentCaseStudy].results.map((result, ridx) => (
                        <div key={ridx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="font-bold text-gray-900 text-sm">{result.metric}</div>
                            <div className="text-green-700 font-semibold text-sm">{result.value}</div>
                            <div className="text-gray-600 text-xs">{result.impact}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {caseStudies[currentCaseStudy].technologies.map((tech) => (
                      <span key={tech} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-lg text-xs font-semibold">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Team: {caseStudies[currentCaseStudy].teamSize}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Duration: {caseStudies[currentCaseStudy].duration}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center gap-6 mt-8">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentCaseStudy(currentCaseStudy === 0 ? caseStudies.length - 1 : currentCaseStudy - 1)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white p-3 rounded-full transition-all hover:scale-110 shadow-lg"
                aria-label="Previous case study"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Dot Indicators */}
              <div className="flex items-center gap-2">
                {caseStudies.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentCaseStudy(idx)}
                    className={`transition-all ${
                      idx === currentCaseStudy
                        ? 'w-12 h-3 bg-gradient-to-r from-purple-600 to-indigo-600'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                    } rounded-full`}
                    aria-label={`Go to case study ${idx + 1}`}
                  />
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentCaseStudy((currentCaseStudy + 1) % caseStudies.length)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white p-3 rounded-full transition-all hover:scale-110 shadow-lg"
                aria-label="Next case study"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Indicator */}
            <div className="text-center mt-4">
              <p className="text-sm text-gray-500 font-medium">
                {currentCaseStudy + 1} of {caseStudies.length}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-24 bg-gradient-to-br from-purple-600 to-indigo-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Client Testimonials</h2>
            <div className="w-24 h-1.5 bg-white/30 mx-auto rounded-full mb-6"></div>
            <p className="text-xl text-purple-100">
              What clients say about working with me
            </p>
          </div>

          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-gray-900">
              <Quote className="w-16 h-16 text-purple-200 mb-6" />
              
              <div className="grid md:grid-cols-3 gap-8 items-start">
                <div className="md:col-span-2">
                  <p className="text-xl leading-relaxed mb-6 italic">
                    "{testimonials[currentTestimonial].content}"
                  </p>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <div>
                    <div className="font-bold text-xl">{testimonials[currentTestimonial].name}</div>
                    <div className="text-purple-600 font-semibold">{testimonials[currentTestimonial].role}</div>
                    <div className="text-gray-600 text-sm">{testimonials[currentTestimonial].company}</div>
                    <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold mt-2">
                      {testimonials[currentTestimonial].project}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <img 
                    src={testimonials[currentTestimonial].image} 
                    alt={testimonials[currentTestimonial].name}
                    className="w-48 h-48 rounded-2xl object-cover shadow-xl border-4 border-purple-100"
                  />
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${idx === currentTestimonial ? 'bg-purple-600 w-8' : 'bg-gray-300'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&auto=format&fit=crop&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl font-bold mb-6">Ready to Transform Your Infrastructure?</h2>
          <p className="text-xl text-purple-100 mb-8 leading-relaxed">
            Let's discuss how I can help you achieve 99.99% uptime, reduce costs by 40%, and accelerate your cloud journey.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-2">
              Schedule Free Consultation <ArrowRight className="w-6 h-6" />
            </a>
            <a href="#case-studies"
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white px-10 py-5 rounded-full font-bold text-lg transition-all inline-flex items-center gap-2">
              View Case Studies
            </a>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Let's Connect</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full"></div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">Get In Touch</h3>
                <p className="text-gray-600 mb-6">
                  Whether you need cloud migration, DevOps automation, or security compliance - I'm here to help.
                </p>
                <div className="space-y-4">
                  <a href="https://www.linkedin.com/in/aneeshthedevops/" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white">
                      💼
                    </div>
                    <div>
                      <div className="font-semibold">LinkedIn</div>
                      <div className="text-sm text-gray-600">Connect professionally</div>
                    </div>
                  </a>
                  <a href="https://www.upwork.com/freelancers/aneeshthedevops" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white">
                      🟢
                    </div>
                    <div>
                      <div className="font-semibold">Upwork</div>
                      <div className="text-sm text-gray-600">Hire on Upwork</div>
                    </div>
                  </a>
                  <a href="https://github.com/aneesh225819" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white text-xl">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold">GitHub</div>
                      <div className="text-sm text-gray-600">View my code</div>
                    </div>
                  </a>
                  <a href="https://calendly.com/aneeshthedevops/hello-there-intro-call" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-700 hover:text-purple-600 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center text-white">
                      📅
                    </div>
                    <div>
                      <div className="font-semibold">Calendly</div>
                      <div className="text-sm text-gray-600">Schedule a meeting</div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8">
                <h4 className="font-bold mb-4">Quick Response</h4>
                <p className="text-sm text-gray-700 mb-4">
                  Average response time: <span className="font-bold text-purple-600">2 hours</span>
                </p>
                <p className="text-sm text-gray-700 mb-4">
                  Availability: <span className="font-bold text-purple-600">Mon-Fri, 9AM-10PM IST</span>
                </p>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-green-600 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Currently Available</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Ready to start immediately
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <p className="text-gray-400 mb-2">© 2025 BuildYourDevOps.com | Aneesh Kumar</p>
          <div className="flex justify-center gap-6 mb-4">
            <a href="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy Policy</a>
            <a href="mailto:support@buildyourdevops.com" className="text-gray-400 hover:text-white text-sm transition-colors">Contact</a>
          </div>
          <p className="text-gray-500 text-sm">Building secure, scalable cloud infrastructure with excellence</p>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-purple-500/50 z-50 group"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6 group-hover:animate-bounce" />
        </button>
      )}
    </div>
  );
}


